/**
 * HedgeScenarios.jsx
 *
 * Scenario analysis for oil hedging at Aviator Energy.
 *
 * Usage:
 *   <Route path="/hedge/scenarios" element={<HedgeScenarios />} />
 *
 * API: GET /api/hedge/scenarios
 * Returns: { scenarios: [...], runDate: "YYYY-MM-DD" }
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine, Cell
} from 'recharts'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'

// ─── Constants ────────────────────────────────────────────────────────────────

const SCENARIO_PRICES = [40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100, 110, 120]

const HEDGE_TYPES = [
  { key: 'SwapPnL',         label: 'Swap',           color: '#3b82f6' },
  { key: 'PutPnL',          label: 'Put',             color: '#a855f7' },
  { key: 'CollarPnL',       label: 'Collar',          color: '#f59e0b' },
  { key: 'ThreeWayPnL',     label: 'Three-Way',       color: '#ec4899' },
  { key: 'ParticipatingPnL',label: 'Participating',   color: '#14b8a6' },
  { key: 'SwaptionPnL',     label: 'Swaption',        color: '#f97316' },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatMonth(dateStr) {
  if (!dateStr) return ''
  const clean = typeof dateStr === 'string' ? dateStr.slice(0, 10) : new Date(dateStr).toISOString().slice(0, 10)
  const d = new Date(clean + 'T00:00:00')
  if (isNaN(d)) return ''
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

function formatCurrency(val, digits = 0) {
  if (val == null || isNaN(val)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(val)
}

function formatNumber(val, digits = 0) {
  if (val == null || isNaN(val)) return '—'
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(val)
}

/**
 * Interpolate between API scenario rows to get a breakdown at any WTI price.
 *
 * Uses the API's pre-computed TotalHedgePnL which correctly handles all structure
 * types (put, swap, collar, etc.):
 *   - Bought put above strike: only loses premium (not capped at floor)
 *   - Bought put below strike: floor protection kicks in
 *   - Swap: locked price, positive or negative P&L vs market
 *
 * Physical revenue = ALL production × spot price (instrument P&L is separate).
 *
 * @param {Object} scenariosForMonth  Map of { [scenarioPrice]: scenarioRow }
 * @param {number} targetPrice        WTI price to compute at
 */
function interpolateBreakdown(scenariosForMonth, targetPrice) {
  if (!scenariosForMonth) return null
  const entries = Object.entries(scenariosForMonth)
    .map(([p, row]) => ({ price: Number(p), row }))
    .sort((a, b) => a.price - b.price)
  if (entries.length === 0) return null

  // Find bounding entries for interpolation
  let lo = entries[0], hi = entries[entries.length - 1]
  for (let i = 0; i < entries.length - 1; i++) {
    if (entries[i].price <= targetPrice && entries[i + 1].price >= targetPrice) {
      lo = entries[i]; hi = entries[i + 1]; break
    }
  }
  if (lo.price > targetPrice) lo = hi
  if (hi.price < targetPrice) hi = lo

  const t = lo.price === hi.price ? 0 : (targetPrice - lo.price) / (hi.price - lo.price)
  const lerp = (a, b) => a + t * (b - a)

  const netOilBBL   = lo.row.NetOilBBL
  const hedgePnL    = lerp(lo.row.TotalHedgePnL, hi.row.TotalHedgePnL)
  const unhedgedRevenue = netOilBBL * targetPrice   // all production sells at market
  const totalRevenue    = unhedgedRevenue + hedgePnL
  const effectivePrice  = netOilBBL > 0 ? totalRevenue / netOilBBL : targetPrice

  return {
    netOilBBL,
    hedgedBBL:    lo.row.HedgedBBL,
    unhedgedBBL:  lo.row.UnhedgedBBL,
    coveragePct:  lo.row.CoveragePct,
    unhedgedRevenue,
    hedgePnL,
    totalRevenue,
    effectivePrice,
    weightedFloor: lo.row.WeightedFloor,
  }
}

/** Determine heat-map cell color class based on effective $/BBL */
function heatColor(effectivePrice, isDark) {
  if (effectivePrice == null) return isDark ? 'bg-slate-800 text-slate-500' : 'bg-gray-100 text-gray-400'
  if (effectivePrice < 55)  return 'bg-red-900/70 text-red-200'
  if (effectivePrice < 65)  return 'bg-orange-900/70 text-orange-200'
  if (effectivePrice < 75)  return 'bg-yellow-800/70 text-yellow-200'
  return 'bg-green-900/70 text-green-200'
}

/** Find the closest column index to the current strip price */
function closestPriceIndex(strip) {
  let best = 0
  let bestDiff = Infinity
  SCENARIO_PRICES.forEach((p, i) => {
    const d = Math.abs(p - strip)
    if (d < bestDiff) { bestDiff = d; best = i }
  })
  return best
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function LoadingSpinner({ isDark }) {
  return (
    <div className="flex items-center justify-center h-64">
      <div className={`w-10 h-10 border-4 rounded-full animate-spin ${
        isDark
          ? 'border-slate-600 border-t-blue-400'
          : 'border-gray-200 border-t-blue-500'
      }`} />
    </div>
  )
}

function ErrorBanner({ message, isDark }) {
  return (
    <div className={`rounded-lg p-4 border ${
      isDark ? 'bg-red-900/30 border-red-700 text-red-300' : 'bg-red-50 border-red-300 text-red-700'
    }`}>
      <p className="font-medium">Failed to load hedge scenarios</p>
      <p className="text-sm mt-1 opacity-80">{message}</p>
    </div>
  )
}

/** Custom tooltip for the heat-map (shown on row hover via React state, not Recharts) */
function HeatMapTooltip({ row, price, breakdown, isDark, style }) {
  if (!breakdown) return null
  return (
    <div
      style={style}
      className={`z-50 rounded-lg shadow-xl border p-3 text-xs w-52 pointer-events-none ${
        isDark ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-white border-gray-200 text-gray-800'
      }`}
    >
      <p className="font-semibold mb-2">
        {formatMonth(row.ContractMonth)} @ ${price}/BBL
      </p>
      <div className="space-y-1">
        <div className="flex justify-between">
          <span className="opacity-70">Net BBL</span>
          <span>{formatNumber(breakdown.netOilBBL)}</span>
        </div>
        <div className="flex justify-between">
          <span className="opacity-70">Hedged BBL</span>
          <span>{formatNumber(breakdown.hedgedBBL)}</span>
        </div>
        <div className="flex justify-between">
          <span className="opacity-70">Coverage</span>
          <span>{(breakdown.coveragePct * 100).toFixed(1)}%</span>
        </div>
        <hr className={isDark ? 'border-slate-600' : 'border-gray-200'} />
        <div className="flex justify-between">
          <span className="opacity-70">Physical Rev</span>
          <span>{formatCurrency(breakdown.unhedgedRevenue)}</span>
        </div>
        <div className="flex justify-between">
          <span className={breakdown.hedgePnL >= 0 ? 'text-green-400' : 'text-red-400'}>
            Hedge P&L
          </span>
          <span className={breakdown.hedgePnL >= 0 ? 'text-green-400' : 'text-red-400'}>
            {formatCurrency(breakdown.hedgePnL)}
          </span>
        </div>
        <div className="flex justify-between font-semibold">
          <span>Total Rev</span>
          <span>{formatCurrency(breakdown.totalRevenue)}</span>
        </div>
        <hr className={isDark ? 'border-slate-600' : 'border-gray-200'} />
        <div className="flex justify-between font-bold text-sm">
          <span>Eff $/BBL</span>
          <span>${breakdown.effectivePrice.toFixed(2)}</span>
        </div>
      </div>
    </div>
  )
}

/** Recharts custom tooltip for payoff chart */
function PayoffTooltip({ active, payload, label, isDark }) {
  if (!active || !payload?.length) return null
  return (
    <div className={`rounded-lg shadow-xl border p-3 text-xs w-48 ${
      isDark ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-white border-gray-200 text-gray-800'
    }`}>
      <p className="font-semibold mb-2">WTI ${label}/BBL</p>
      {payload.map(p => (
        <div key={p.dataKey} className="flex justify-between gap-3" style={{ color: p.color }}>
          <span>{p.name}</span>
          <span>${Number(p.value).toFixed(2)}</span>
        </div>
      ))}
    </div>
  )
}

/** Recharts custom tooltip for waterfall/bar chart */
function WaterfallTooltip({ active, payload, label, isDark }) {
  if (!active || !payload?.length) return null
  return (
    <div className={`rounded-lg shadow-xl border p-3 text-xs w-44 ${
      isDark ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-white border-gray-200 text-gray-800'
    }`}>
      <p className="font-semibold mb-2">{label}</p>
      {payload.map(p => (
        <div key={p.dataKey} className="flex justify-between gap-3" style={{ color: p.fill || p.color }}>
          <span>{p.name}</span>
          <span>{formatCurrency(p.value)}</span>
        </div>
      ))}
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function HedgeScenarios() {
  const navigate = useNavigate()
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  // ── State ──────────────────────────────────────────────────────────────────
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState(null)
  const [scenarios, setScenarios] = useState([])
  const [runDate, setRunDate]   = useState('')
  const [selectedMonth, setSelectedMonth] = useState('all')

  // Tooltip state for heat map
  const [tooltip, setTooltip] = useState(null)
  // { row, price, breakdown, x, y }

  // ── Fetch data ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    async function fetchData() {
      try {
        setLoading(true)
        setError(null)
        const res = await fetch(`${apiConfig.baseUrl}/hedge/scenarios`)
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`)
        const data = await res.json()
        if (!cancelled) {
          setScenarios(data.scenarios || [])
          setRunDate(data.runDate || '')
        }
      } catch (err) {
        if (!cancelled) setError(err.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchData()
    return () => { cancelled = true }
  }, [])

  // ── Derived data ───────────────────────────────────────────────────────────

  /**
   * Full scenario lookup: { [contractMonth]: { [scenarioPrice]: row } }
   * Used to look up pre-computed API values directly (avoids swap approximation).
   */
  const scenariosByMonthPrice = useMemo(() => {
    const map = {}
    scenarios.forEach(s => {
      if (!map[s.ContractMonth]) map[s.ContractMonth] = {}
      map[s.ContractMonth][s.ScenarioPrice] = s
    })
    return map
  }, [scenarios])

  /**
   * De-duplicate by ContractMonth — take the row for each month
   * that has the closest ScenarioPrice to the actual strip price.
   * This gives us one "base" row per month from which we extrapolate.
   */
  const baseRowByMonth = useMemo(() => {
    const map = {}
    scenarios.forEach(s => {
      const m = s.ContractMonth
      if (!map[m]) {
        map[m] = s
      } else {
        // prefer the row whose ScenarioPrice is closest to CurrentStripPrice
        const prevDiff = Math.abs(map[m].ScenarioPrice - s.CurrentStripPrice)
        const curDiff  = Math.abs(s.ScenarioPrice - s.CurrentStripPrice)
        if (curDiff < prevDiff) map[m] = s
      }
    })
    return map
  }, [scenarios])

  /** Hedged months — those with at least 1 BBL hedged */
  const hedgedMonths = useMemo(() => {
    return Object.entries(baseRowByMonth)
      .filter(([, row]) => row.HedgedBBL > 0)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month]) => month)
  }, [baseRowByMonth])

  /** Strip price — take from first available row */
  const stripPrice = useMemo(() => {
    const first = scenarios.find(s => s.CurrentStripPrice != null)
    return first ? first.CurrentStripPrice : null
  }, [scenarios])

  const stripColIndex = useMemo(() => {
    if (stripPrice == null) return null
    return closestPriceIndex(stripPrice)
  }, [stripPrice])

  /** Selected month row (for payoff chart and waterfall) */
  const selectedRow = useMemo(() => {
    if (selectedMonth === 'all') return hedgedMonths.length > 0 ? baseRowByMonth[hedgedMonths[0]] : null
    return baseRowByMonth[selectedMonth] || null
  }, [selectedMonth, hedgedMonths, baseRowByMonth])

  /**
   * Payoff chart data — X = SCENARIO_PRICES, one line per active hedge type + Unhedged
   * Uses API's pre-computed EffectivePrice directly (correct for puts, collars, etc.)
   */
  const payoffData = useMemo(() => {
    if (!selectedRow) return []
    const monthScenarios = scenariosByMonthPrice[selectedRow.ContractMonth] || {}
    return SCENARIO_PRICES.map(price => {
      const point = { price, Unhedged: price }
      const row = monthScenarios[price]
      if (row) {
        point['Effective'] = parseFloat(row.EffectivePrice.toFixed(2))
      }
      return point
    })
  }, [selectedRow, scenariosByMonthPrice])

  /** Active hedge types for selected month */
  const activeHedgeTypes = useMemo(() => {
    if (!selectedRow) return []
    return HEDGE_TYPES.filter(ht => selectedRow[ht.key] != null && selectedRow[ht.key] !== 0)
  }, [selectedRow])

  /**
   * Waterfall data for the 3-card revenue summary at current strip price.
   * Uses interpolation between API scenario rows so put upside is correct.
   */
  const waterfallData = useMemo(() => {
    if (!selectedRow || stripPrice == null) return []
    const bd = interpolateBreakdown(scenariosByMonthPrice[selectedRow.ContractMonth], stripPrice)
    if (!bd) return []
    return [
      { name: 'Physical Revenue', value: bd.unhedgedRevenue, fill: '#3b82f6' },
      { name: 'Hedge P&L',        value: bd.hedgePnL,        fill: bd.hedgePnL >= 0 ? '#22c55e' : '#ef4444' },
      { name: 'Net Revenue',      value: bd.totalRevenue,    fill: '#8b5cf6' },
    ]
  }, [selectedRow, stripPrice, scenariosByMonthPrice])

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleCellMouseEnter = useCallback((e, row, price) => {
    // Use direct API lookup for exact scenario price (always available in heat map)
    const scenarioRow = scenariosByMonthPrice[row.ContractMonth]?.[price]
    const bd = scenarioRow ? {
      netOilBBL:      scenarioRow.NetOilBBL,
      hedgedBBL:      scenarioRow.HedgedBBL,
      unhedgedBBL:    scenarioRow.UnhedgedBBL,
      coveragePct:    scenarioRow.CoveragePct,
      unhedgedRevenue: scenarioRow.NetOilBBL * price,
      hedgePnL:       scenarioRow.TotalHedgePnL,
      totalRevenue:   scenarioRow.NetOilBBL * price + scenarioRow.TotalHedgePnL,
      effectivePrice: scenarioRow.EffectivePrice,
      weightedFloor:  scenarioRow.WeightedFloor,
    } : null
    const rect = e.currentTarget.getBoundingClientRect()
    setTooltip({
      row, price, breakdown: bd,
      x: rect.right + 8,
      y: rect.top,
    })
  }, [scenariosByMonthPrice])

  const handleCellMouseLeave = useCallback(() => {
    setTooltip(null)
  }, [])

  // ── Theme classes ──────────────────────────────────────────────────────────
  const bg       = isDark ? 'bg-slate-900' : 'bg-gray-50'
  const cardBg   = isDark ? 'bg-slate-800' : 'bg-white'
  const border   = isDark ? 'border-slate-700' : 'border-gray-200'
  const text      = isDark ? 'text-white' : 'text-gray-900'
  const textMuted = isDark ? 'text-slate-400' : 'text-gray-500'
  const textSm    = isDark ? 'text-slate-300' : 'text-gray-700'
  const selectBg  = isDark ? 'bg-slate-700 text-white border-slate-600' : 'bg-white text-gray-900 border-gray-300'
  const axisColor = isDark ? '#94a3b8' : '#6b7280'
  const gridColor = isDark ? '#334155' : '#e5e7eb'

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={`min-h-screen ${bg} ${text} p-4 sm:p-6`}>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/hedge')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
            isDark
              ? 'border-slate-600 text-slate-300 hover:bg-slate-700'
              : 'border-gray-300 text-gray-600 hover:bg-gray-100'
          }`}
          aria-label="Back to Hedge"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Hedge
        </button>

        <div>
          <h1 className={`text-xl font-bold ${text}`}>Hedge Scenarios</h1>
          {runDate && (
            <p className={`text-xs ${textMuted}`}>
              Run date: {runDate}
              {stripPrice != null && (
                <span className="ml-3">
                  Strip: <span className="font-semibold text-blue-400">${stripPrice.toFixed(2)}/BBL</span>
                </span>
              )}
            </p>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <label htmlFor="month-select" className={`text-sm ${textMuted}`}>Month</label>
          <select
            id="month-select"
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value)}
            className={`text-sm rounded-lg px-3 py-1.5 border focus:outline-none focus:ring-2 focus:ring-blue-500 ${selectBg}`}
            aria-label="Select contract month"
          >
            <option value="all">All Hedged Months</option>
            {hedgedMonths.map(m => (
              <option key={m} value={m}>{formatMonth(m)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Loading / Error ──────────────────────────────────────────────────── */}
      {loading && <LoadingSpinner isDark={isDark} />}
      {!loading && error && <ErrorBanner message={error} isDark={isDark} />}

      {!loading && !error && (
        <div className="space-y-6">

          {/* ── Section 1: Heat Map ─────────────────────────────────────────── */}
          <section
            className={`${cardBg} rounded-xl border ${border} overflow-hidden`}
            aria-labelledby="heatmap-title"
          >
            <div className={`px-4 py-3 border-b ${border} flex items-center justify-between`}>
              <h2 id="heatmap-title" className={`font-semibold ${text}`}>
                Effective Price Heat Map
                <span className={`ml-2 text-xs font-normal ${textMuted}`}>
                  $/BBL at scenario WTI price
                </span>
              </h2>
              {/* Legend */}
              <div className="flex items-center gap-3 text-xs flex-wrap">
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded bg-red-900/70" />
                  <span className={textMuted}>&lt;$55</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded bg-orange-900/70" />
                  <span className={textMuted}>$55-65</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded bg-yellow-800/70" />
                  <span className={textMuted}>$65-75</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded bg-green-900/70" />
                  <span className={textMuted}>&gt;$75</span>
                </span>
              </div>
            </div>

            {hedgedMonths.length === 0 ? (
              <div className={`p-8 text-center ${textMuted}`}>
                No hedged months available.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse" role="grid" aria-label="Hedge scenario heat map">
                  <thead>
                    <tr>
                      <th
                        className={`sticky left-0 z-10 px-3 py-2 text-left font-semibold ${
                          isDark ? 'bg-slate-800 text-slate-300' : 'bg-white text-gray-700'
                        } border-b ${border} min-w-[90px]`}
                      >
                        Month
                      </th>
                      {SCENARIO_PRICES.map((p, i) => (
                        <th
                          key={p}
                          className={`px-2 py-2 text-center font-semibold border-b ${border} min-w-[52px] ${
                            i === stripColIndex
                              ? 'bg-blue-600/30 text-blue-300'
                              : isDark ? 'text-slate-300' : 'text-gray-700'
                          }`}
                          scope="col"
                        >
                          ${p}
                          {i === stripColIndex && (
                            <span className="block text-[9px] text-blue-400 font-normal leading-none">
                              strip
                            </span>
                          )}
                        </th>
                      ))}
                      <th
                        className={`px-2 py-2 text-center font-semibold border-b ${border} min-w-[60px] ${
                          isDark ? 'text-slate-300' : 'text-gray-700'
                        }`}
                        scope="col"
                      >
                        Coverage
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {hedgedMonths.map((month, rowIdx) => {
                      const row = baseRowByMonth[month]
                      return (
                        <tr
                          key={month}
                          className={rowIdx % 2 === 0
                            ? isDark ? 'bg-slate-800/50' : 'bg-white'
                            : isDark ? 'bg-slate-800/20' : 'bg-gray-50'
                          }
                        >
                          {/* Month label — sticky */}
                          <td
                            className={`sticky left-0 z-10 px-3 py-1.5 font-medium ${
                              isDark ? 'bg-slate-800 text-slate-200' : 'bg-white text-gray-800'
                            } border-r ${border}`}
                          >
                            {formatMonth(month)}
                          </td>

                          {/* Price scenario cells */}
                          {SCENARIO_PRICES.map((price, colIdx) => {
                            const ep = scenariosByMonthPrice[month]?.[price]?.EffectivePrice ?? null
                            const colorClass = heatColor(ep, isDark)
                            return (
                              <td
                                key={price}
                                className={`px-1 py-1.5 text-center cursor-default transition-opacity hover:opacity-80 ${colorClass} ${
                                  colIdx === stripColIndex ? 'ring-1 ring-inset ring-blue-400/50' : ''
                                }`}
                                onMouseEnter={e => handleCellMouseEnter(e, row, price)}
                                onMouseLeave={handleCellMouseLeave}
                                role="gridcell"
                                aria-label={`${formatMonth(month)} at $${price}: ${ep != null ? '$' + ep.toFixed(2) : 'N/A'}`}
                              >
                                {ep != null ? ep.toFixed(2) : '—'}
                              </td>
                            )
                          })}

                          {/* Coverage % */}
                          <td className={`px-2 py-1.5 text-center ${textSm}`}>
                            {(row.CoveragePct * 100).toFixed(1)}%
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ── Sections 2 & 3 side-by-side on wide screens ─────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* ── Section 2: Payoff Chart ───────────────────────────────────── */}
            <section
              className={`${cardBg} rounded-xl border ${border} p-4`}
              aria-labelledby="payoff-title"
            >
              <h2 id="payoff-title" className={`font-semibold mb-1 ${text}`}>
                Payoff Diagram
                {selectedRow && (
                  <span className={`ml-2 text-sm font-normal ${textMuted}`}>
                    {formatMonth(selectedRow.ContractMonth)}
                  </span>
                )}
              </h2>
              <p className={`text-xs ${textMuted} mb-4`}>
                Effective $/BBL vs WTI scenario price
              </p>

              {!selectedRow ? (
                <div className={`h-72 flex items-center justify-center ${textMuted} text-sm`}>
                  No hedged month selected
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart
                    data={payoffData}
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                    <XAxis
                      dataKey="price"
                      tickFormatter={v => `$${v}`}
                      tick={{ fill: axisColor, fontSize: 11 }}
                      label={{ value: 'WTI Price ($/BBL)', position: 'insideBottom', offset: -2, fill: axisColor, fontSize: 11 }}
                      height={40}
                    />
                    <YAxis
                      tickFormatter={v => `$${v}`}
                      tick={{ fill: axisColor, fontSize: 11 }}
                      domain={['auto', 'auto']}
                      width={55}
                    />
                    <Tooltip content={<PayoffTooltip isDark={isDark} />} />
                    <Legend
                      wrapperStyle={{ paddingTop: 8, fontSize: 12 }}
                    />

                    {/* Current strip reference line */}
                    {stripPrice != null && (
                      <ReferenceLine
                        x={SCENARIO_PRICES[stripColIndex]}
                        stroke="#3b82f6"
                        strokeDasharray="4 3"
                        label={{ value: 'Strip', position: 'top', fill: '#3b82f6', fontSize: 10 }}
                      />
                    )}

                    {/* Unhedged reference line (45 degree) */}
                    <Line
                      type="linear"
                      dataKey="Unhedged"
                      stroke={isDark ? '#64748b' : '#9ca3af'}
                      strokeDasharray="6 3"
                      strokeWidth={1.5}
                      dot={false}
                      name="Unhedged"
                    />

                    {/* Effective (hedged) line */}
                    <Line
                      type="monotone"
                      dataKey="Effective"
                      stroke="#22c55e"
                      strokeWidth={2.5}
                      dot={false}
                      name="Effective (Hedged)"
                      activeDot={{ r: 5, strokeWidth: 0 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}

              {/* Position summary below chart */}
              {selectedRow && (
                <div className={`mt-3 grid grid-cols-3 gap-2 text-xs border-t ${border} pt-3`}>
                  <div className="text-center">
                    <p className={textMuted}>Hedged BBL</p>
                    <p className="font-semibold">{formatNumber(selectedRow.HedgedBBL)}</p>
                  </div>
                  <div className="text-center">
                    <p className={textMuted}>Weighted Floor</p>
                    <p className="font-semibold">${selectedRow.WeightedFloor?.toFixed(2)}</p>
                  </div>
                  <div className="text-center">
                    <p className={textMuted}>Coverage</p>
                    <p className="font-semibold">{(selectedRow.CoveragePct * 100).toFixed(1)}%</p>
                  </div>
                </div>
              )}
            </section>

            {/* ── Section 3: Revenue Summary at Strip Price ─────────────────── */}
            <section
              className={`${cardBg} rounded-xl border ${border} p-4`}
              aria-labelledby="revenue-title"
            >
              <h2 id="revenue-title" className={`font-semibold mb-1 ${text}`}>
                Revenue at Strip Price
                {stripPrice != null && (
                  <span className={`ml-2 text-sm font-normal ${textMuted}`}>
                    ${stripPrice.toFixed(2)}/BBL
                  </span>
                )}
              </h2>
              <p className={`text-xs ${textMuted} mb-4`}>
                {selectedRow ? formatMonth(selectedRow.ContractMonth) : 'Select a month above'}
              </p>

              {!selectedRow || stripPrice == null ? (
                <div className={`h-72 flex items-center justify-center ${textMuted} text-sm`}>
                  No data available
                </div>
              ) : (() => {
                const bd = interpolateBreakdown(scenariosByMonthPrice[selectedRow.ContractMonth], stripPrice)
                if (!bd) return null
                return (
                  <>
                    {/* Three summary cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                      <div className={`rounded-lg p-3 border ${border} ${isDark ? 'bg-slate-700/50' : 'bg-blue-50'}`}>
                        <p className={`text-xs ${textMuted} mb-1`}>Physical Revenue</p>
                        <p className={`text-base font-bold text-blue-400`}>
                          {formatCurrency(bd.unhedgedRevenue)}
                        </p>
                        <p className={`text-xs ${textMuted} mt-1`}>
                          {formatNumber(bd.unhedgedBBL)} BBL x ${stripPrice.toFixed(2)}
                        </p>
                      </div>
                      <div className={`rounded-lg p-3 border ${border} ${
                        isDark ? 'bg-slate-700/50' : bd.hedgePnL >= 0 ? 'bg-green-50' : 'bg-red-50'
                      }`}>
                        <p className={`text-xs ${textMuted} mb-1`}>Hedge P&L</p>
                        <p className={`text-base font-bold ${bd.hedgePnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {bd.hedgePnL >= 0 ? '+' : ''}{formatCurrency(bd.hedgePnL)}
                        </p>
                        <p className={`text-xs ${textMuted} mt-1`}>
                          {formatNumber(bd.hedgedBBL)} BBL @ ${bd.weightedFloor?.toFixed(2)} floor
                        </p>
                      </div>
                      <div className={`rounded-lg p-3 border ${border} ${isDark ? 'bg-slate-700/50' : 'bg-purple-50'}`}>
                        <p className={`text-xs ${textMuted} mb-1`}>Net Revenue</p>
                        <p className="text-base font-bold text-purple-400">
                          {formatCurrency(bd.totalRevenue)}
                        </p>
                        <p className={`text-xs ${textMuted} mt-1`}>
                          Eff ${bd.effectivePrice.toFixed(2)}/BBL
                        </p>
                      </div>
                    </div>

                    {/* Bar chart for visual comparison */}
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart
                        data={waterfallData}
                        margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                        barCategoryGap="30%"
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                        <XAxis
                          dataKey="name"
                          tick={{ fill: axisColor, fontSize: 11 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          tickFormatter={v => `$${(v / 1e6).toFixed(1)}M`}
                          tick={{ fill: axisColor, fontSize: 11 }}
                          width={55}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip content={<WaterfallTooltip isDark={isDark} />} />
                        <Bar dataKey="value" name="Revenue" radius={[4, 4, 0, 0]}>
                          {waterfallData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} fillOpacity={0.85} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </>
                )
              })()}
            </section>
          </div>

          {/* ── Section 4: All Months Summary Table ─────────────────────────── */}
          {hedgedMonths.length > 0 && (
            <section
              className={`${cardBg} rounded-xl border ${border} overflow-hidden`}
              aria-labelledby="summary-title"
            >
              <div className={`px-4 py-3 border-b ${border}`}>
                <h2 id="summary-title" className={`font-semibold ${text}`}>
                  Position Summary
                  <span className={`ml-2 text-xs font-normal ${textMuted}`}>
                    At current strip price
                    {stripPrice != null && ` ($${stripPrice.toFixed(2)}/BBL)`}
                  </span>
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" role="table" aria-label="Hedge position summary">
                  <thead>
                    <tr className={`text-xs ${textMuted} border-b ${border}`}>
                      <th className="px-4 py-2 text-left font-medium">Month</th>
                      <th className="px-4 py-2 text-right font-medium">Net BBL</th>
                      <th className="px-4 py-2 text-right font-medium">Hedged BBL</th>
                      <th className="px-4 py-2 text-right font-medium">Coverage</th>
                      <th className="px-4 py-2 text-right font-medium">Wtd Floor</th>
                      <th className="px-4 py-2 text-right font-medium">Physical Rev</th>
                      <th className="px-4 py-2 text-right font-medium">Hedge P&L</th>
                      <th className="px-4 py-2 text-right font-medium">Net Revenue</th>
                      <th className="px-4 py-2 text-right font-medium">Eff $/BBL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hedgedMonths.map((month, i) => {
                      const row = baseRowByMonth[month]
                      const bd  = stripPrice != null ? interpolateBreakdown(scenariosByMonthPrice[month], stripPrice) : null
                      return (
                        <tr
                          key={month}
                          className={`border-b ${border} transition-colors cursor-pointer ${
                            selectedMonth === month
                              ? isDark ? 'bg-blue-900/25' : 'bg-blue-50'
                              : i % 2 === 0
                                ? isDark ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50'
                                : isDark ? 'bg-slate-800/30 hover:bg-slate-700/30' : 'bg-gray-50/50 hover:bg-gray-100'
                          }`}
                          onClick={() => setSelectedMonth(month)}
                          role="row"
                          aria-selected={selectedMonth === month}
                          tabIndex={0}
                          onKeyDown={e => e.key === 'Enter' && setSelectedMonth(month)}
                        >
                          <td className={`px-4 py-2.5 font-medium ${text}`}>{formatMonth(month)}</td>
                          <td className={`px-4 py-2.5 text-right ${textSm}`}>{formatNumber(row.NetOilBBL)}</td>
                          <td className={`px-4 py-2.5 text-right ${textSm}`}>{formatNumber(row.HedgedBBL)}</td>
                          <td className={`px-4 py-2.5 text-right ${textSm}`}>{(row.CoveragePct * 100).toFixed(1)}%</td>
                          <td className={`px-4 py-2.5 text-right ${textSm}`}>${row.WeightedFloor?.toFixed(2)}</td>
                          <td className={`px-4 py-2.5 text-right ${textSm}`}>
                            {bd ? formatCurrency(bd.unhedgedRevenue) : '—'}
                          </td>
                          <td className={`px-4 py-2.5 text-right font-medium ${
                            bd ? (bd.hedgePnL >= 0 ? 'text-green-400' : 'text-red-400') : textSm
                          }`}>
                            {bd ? `${bd.hedgePnL >= 0 ? '+' : ''}${formatCurrency(bd.hedgePnL)}` : '—'}
                          </td>
                          <td className={`px-4 py-2.5 text-right font-medium ${textSm}`}>
                            {bd ? formatCurrency(bd.totalRevenue) : '—'}
                          </td>
                          <td className={`px-4 py-2.5 text-right font-semibold ${
                            bd && bd.effectivePrice > (stripPrice || 0) ? 'text-green-400' : textSm
                          }`}>
                            {bd ? `$${bd.effectivePrice.toFixed(2)}` : '—'}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>

                  {/* Totals row */}
                  {stripPrice != null && (() => {
                    let totNetBBL = 0, totHedgedBBL = 0, totPhysRev = 0, totHedgePnL = 0, totNetRev = 0
                    hedgedMonths.forEach(month => {
                      const row = baseRowByMonth[month]
                      const bd  = interpolateBreakdown(scenariosByMonthPrice[month], stripPrice)
                      if (!bd) return
                      totNetBBL    += row.NetOilBBL
                      totHedgedBBL += row.HedgedBBL
                      totPhysRev   += bd.unhedgedRevenue
                      totHedgePnL  += bd.hedgePnL
                      totNetRev    += bd.totalRevenue
                    })
                    const totEff = totNetBBL > 0 ? totNetRev / totNetBBL : 0
                    return (
                      <tfoot>
                        <tr className={`border-t-2 ${isDark ? 'border-slate-600' : 'border-gray-300'} ${
                          isDark ? 'bg-slate-700/40' : 'bg-gray-100'
                        } font-semibold text-sm`}>
                          <td className={`px-4 py-2.5 ${text}`}>Total</td>
                          <td className={`px-4 py-2.5 text-right ${text}`}>{formatNumber(totNetBBL)}</td>
                          <td className={`px-4 py-2.5 text-right ${text}`}>{formatNumber(totHedgedBBL)}</td>
                          <td className={`px-4 py-2.5 text-right ${text}`}>
                            {totNetBBL > 0 ? ((totHedgedBBL / totNetBBL) * 100).toFixed(1) : '0.0'}%
                          </td>
                          <td className={`px-4 py-2.5 text-right ${textMuted}`}>—</td>
                          <td className={`px-4 py-2.5 text-right ${text}`}>{formatCurrency(totPhysRev)}</td>
                          <td className={`px-4 py-2.5 text-right ${totHedgePnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {totHedgePnL >= 0 ? '+' : ''}{formatCurrency(totHedgePnL)}
                          </td>
                          <td className={`px-4 py-2.5 text-right ${text}`}>{formatCurrency(totNetRev)}</td>
                          <td className={`px-4 py-2.5 text-right ${text}`}>${totEff.toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    )
                  })()}
                </table>
              </div>
            </section>
          )}
        </div>
      )}

      {/* ── Heat Map Tooltip (portal-style, fixed position) ─────────────────── */}
      {tooltip && (
        <div
          className="fixed"
          style={{ left: Math.min(tooltip.x, window.innerWidth - 220), top: tooltip.y, zIndex: 9999 }}
          aria-hidden="true"
        >
          <HeatMapTooltip
            row={tooltip.row}
            price={tooltip.price}
            breakdown={tooltip.breakdown}
            isDark={isDark}
          />
        </div>
      )}
    </div>
  )
}
